import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Camera, Upload, KeyRound, AlertTriangle, Check, Eye, EyeOff, ShieldAlert, BellRing } from "lucide-react";
import { toast } from "sonner";
import NotificationPrefs from "@/components/NotificationPrefs";
import CalendarSubscribe from "@/components/CalendarSubscribe";
import SettingsPage from "@/pages/SettingsPage";
import { resizeAvatarToDataUrl } from "@/lib/avatar";

const MAX_DIM = 512;   // longest side after resize
const JPEG_Q = 0.85;

// Aspect-preserving resize — the crop happens in CSS via `object-cover`
// (AvatarImage). Keeps the resulting data URL free of stretching so the same
// upload looks correct in every avatar circle it appears in.
async function resizeToDataUrl(file) {
  return resizeAvatarToDataUrl(file, { maxDim: MAX_DIM, quality: JPEG_Q });
}

export default function MyInfoPage() {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pw, setPw] = useState({ cur: "", nw: "", cf: "" });
  const [show, setShow] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("That file isn't an image"); return; }
    setUploading(true);
    try {
      const dataUrl = await resizeToDataUrl(f);
      await api.post("/me/avatar", { data_url: dataUrl });
      toast.success("Photo updated");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message || "Upload failed");
    }
    setUploading(false);
    e.target.value = "";  // allow selecting the same file again
  };

  const submitPw = async () => {
    if (!pw.cur || !pw.nw) return toast.error("Fill both password fields");
    if (pw.nw.length < 6) return toast.error("Use at least 6 characters");
    if (pw.nw !== pw.cf)  return toast.error("New passwords don't match");
    setSavingPw(true);
    try {
      await api.put("/me/password", { current_password: pw.cur, new_password: pw.nw });
      toast.success("Password updated");
      setPw({ cur: "", nw: "", cf: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to update");
    }
    setSavingPw(false);
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="my-info-page">
      {/* ---- Header card ---- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-lg font-display font-semibold">My information</div>
        <div className="text-sm text-slate-500 mt-0.5">
          Update your profile photo and password.
        </div>
      </div>

      {/* ---- Photo card ---- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 font-display font-semibold">
          <Camera size={16} className="text-blue-600" /> Profile photo
        </div>
        <div className="mt-3 flex items-center gap-5">
          <Avatar className="w-20 h-20 ring-2 ring-slate-100">
            <AvatarImage src={user?.avatar} data-testid="myinfo-avatar-img" />
            <AvatarFallback className="text-lg">{user?.name?.[0] || "?"}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="user"
                className="hidden"
                onChange={onFile}
                data-testid="myinfo-avatar-input"
              />
              <Button variant="outline" onClick={onPickFile} disabled={uploading} data-testid="myinfo-avatar-upload">
                <Upload size={14} className="mr-1" />
                {uploading ? "Uploading…" : (user?.avatar ? "Change photo" : "Upload photo")}
              </Button>
              <span className="text-[11px] text-slate-500 self-center">
                JPG, PNG or WebP · we&apos;ll shrink it to 512px automatically.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Password card ---- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 font-display font-semibold">
          <KeyRound size={16} className="text-blue-600" /> Change password
        </div>

        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-[12px] text-red-900 flex items-start gap-2" data-testid="myinfo-pw-warning">
          <ShieldAlert size={14} className="mt-0.5 shrink-0" />
          <div>
            <b>PLEASE DO NOT use your Sutter password.</b>  This app is separate from Sutter systems — pick a unique password you don&apos;t use anywhere else.
          </div>
        </div>

        <div className="mt-4 grid gap-3 max-w-md">
          <div>
            <Label>Current password</Label>
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                value={pw.cur}
                onChange={(e) => setPw({ ...pw, cur: e.target.value })}
                autoComplete="current-password"
                data-testid="myinfo-pw-current"
              />
              <button type="button" onClick={() => setShow(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <Label>New password</Label>
            <Input type={show ? "text" : "password"} value={pw.nw}
              onChange={(e) => setPw({ ...pw, nw: e.target.value })}
              autoComplete="new-password"
              data-testid="myinfo-pw-new"
            />
            <div className="text-[11px] text-slate-500 mt-1">At least 6 characters.</div>
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type={show ? "text" : "password"} value={pw.cf}
              onChange={(e) => setPw({ ...pw, cf: e.target.value })}
              autoComplete="new-password"
              data-testid="myinfo-pw-confirm"
            />
            {pw.cf && pw.nw && pw.cf !== pw.nw && (
              <div className="text-[11px] text-red-600 mt-1 inline-flex items-center gap-1">
                <AlertTriangle size={11} /> Passwords don&apos;t match.
              </div>
            )}
            {pw.cf && pw.nw && pw.cf === pw.nw && pw.nw.length >= 6 && (
              <div className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1">
                <Check size={11} /> Match.
              </div>
            )}
          </div>
          <div>
            <Button className="bg-blue-600 hover:bg-blue-700"
              onClick={submitPw} disabled={savingPw} data-testid="myinfo-pw-submit">
              {savingPw ? "Saving…" : "Update password"}
            </Button>
          </div>
        </div>
      </div>

      {/* ---- Notification preferences ---- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="myinfo-notifications">
        <div className="flex items-center gap-2 font-display font-semibold">
          <BellRing size={16} className="text-blue-600" /> Notification preferences
        </div>
        <div className="text-sm text-slate-500 mt-0.5">
          Pick which events send you an email and which just show up in the app bell.
        </div>
        <div className="mt-4">
          <NotificationPrefs userId={null} />
        </div>
      </div>

      {/* ---- Calendar subscribe ---- */}
      <CalendarSubscribe />

      {/* ---- App install + browser notifications (moved from the retired Settings tab) ---- */}
      <SettingsPage />
    </div>
  );
}
